// ---------------------------------------------------------------
// Tracker-integrity alerts: the device went offline (unplugged / no power /
// no signal), came back, or was connected to a DIFFERENT vehicle (VIN change).
// These are the text messages the owner/parent receives.
// ---------------------------------------------------------------

// Text-message templates (consumer-facing)
export const deviceText = {
  disconnect: (name, time, loc) =>
    `⚠️ Tracker offline — ${name}\nThe GPS tracker on ${name} stopped reporting at ${time}${loc ? `, last seen near ${loc}` : ''}. It may be unplugged, powered off, or out of signal. We'll let you know as soon as it's back online.`,
  reconnect: (name, time, loc) =>
    `✅ Tracker back online — ${name}\nThe tracker on ${name} is reporting again as of ${time}${loc ? `, near ${loc}` : ''}.`,
  newVehicle: (name, car = {}) => {
    const desc = [car.year, car.make, car.model].filter(Boolean).join(' ');
    return `🚗 Tracker moved to a new vehicle\nThe tracker "${name}" was just connected to a different vehicle:\n${desc || 'Vehicle details unavailable'}${car.vin ? `\nVIN: ${car.vin}` : ''}`;
  },
  arrived: (name, place, time) => `📍 ${name} arrived at ${place} at ${time}.`,
  left: (name, place, time) => `📍 ${name} left ${place} at ${time}.`,
  lowRange: (name, miles, thr) => `⛽ Low fuel — ${name}\n${name} has about ${miles} miles of range left (under ${thr} mi). Time to fill up soon.`,
  lowFuel: (name, pct) => `⛽ Low fuel warning — ${name}\nThe fuel level on ${name} is low — about ${pct}%. The car's low-fuel light is on. Recommend refueling.`,
  checkEngine: (name, n, codes = []) => (codes && codes.length)
    ? `🛠️ Check engine — ${name}\n${name} is reporting ${codes.map((c) => `${c.code} (${c.meaning})`).join('; ')}. The check-engine light is likely on — have it looked at soon.`
    : `🛠️ Check engine — ${name}\n${name}'s onboard diagnostics reported ${n} trouble code${n === 1 ? '' : 's'} (the check-engine light is likely on). Have it checked soon.`,
  stoppedHighway: (name, mins, road, loc) => `🚨 ${name} stopped on the highway\n${name} has been completely stopped for ${mins}+ minutes on ${road || 'a highway'}${loc ? ` (${loc})` : ''}. This could be a breakdown or accident — check on the driver right away.`,
  towTheft: (name, loc, mapUrl) => `🚨 Possible tow / theft — ${name}\n${name} is moving with the engine off — it may be getting towed or stolen. Location: ${loc || 'unknown'}.${mapUrl ? `\nLive location: ${mapUrl}` : ''}\nIf this isn't expected, call the police.`,
  crash: (name, loc, mapUrl) => `🚨 Crash detected — ${name}\nA hard impact was detected on ${name}. Location: ${loc || 'unknown'}.${mapUrl ? `\nLive location: ${mapUrl}` : ''}\nCheck on the driver right away.`,
};

// Is this reverse-geocoded point on a highway / freeway / interstate?
export function isHighwayRoad(rev) {
  if (!rev) return false;
  if (rev.roadClass === 'highway' && /^(motorway|trunk|primary)(_link)?$/.test(rev.roadType || '')) return true;
  return /interstate|freeway|expressway|turnpike|motorway|\bhwy\b|highway|\bi-?\d|\bus-?\d|state (route|road)|\bsr-?\d|\bfl-?\d/i.test(`${rev.road} ${rev.display}`);
}

// Announce arrivals/departures at the owner's saved ⭐ places.
// Geofences are small circles, so a car near the edge (GPS jitter, parking-lot
// maneuvering) can fire Exit→Enter→Exit within seconds. We collapse that flapping:
// per car+place, only keep a real state change that has held for FLAP_MS.
const FLAP_MS = 120 * 1000;
export function checkPlaceArrivals(devices, events, geofences) {
  const stars = {};
  (geofences || []).forEach((g) => { if ((g.name || '').startsWith('⭐') || (g.name || '').startsWith('⏱')) stars[g.id] = g.name.replace(/^[⭐⏱]\s*/, ''); });
  const out = [];
  // group the relevant enter/exit events by car+place
  const groups = {};
  (events || []).forEach((e) => {
    if ((e.type !== 'geofenceEnter' && e.type !== 'geofenceExit') || !stars[e.geofenceId]) return;
    const k = `${e.deviceId}:${e.geofenceId}`;
    (groups[k] = groups[k] || []).push(e);
  });
  Object.values(groups).forEach((list) => {
    list.sort((a, b) => new Date(a.eventTime) - new Date(b.eventTime));
    let lastKeptT = -Infinity; let lastType = null;
    list.forEach((e) => {
      const t = new Date(e.eventTime).getTime();
      // keep only a genuine, settled transition (different from the last kept
      // state and at least FLAP_MS after it); skip rapid reversals
      if (e.type !== lastType && (t - lastKeptT) >= FLAP_MS) {
        lastKeptT = t; lastType = e.type;
        const place = stars[e.geofenceId];
        const dev = devices.find((d) => d.id === e.deviceId);
        const name = dev ? dev.name : 'Car';
        const time = new Date(e.eventTime).toLocaleTimeString();
        const arrived = e.type === 'geofenceEnter';
        out.push({
          key: `place:${e.id}`, entity: name, severity: 'info', icon: arrived ? '➡️' : '⬅️',
          title: arrived ? `Arrived at ${place}` : `Left ${place}`,
          info: `${name} ${arrived ? 'arrived at' : 'left'} ${place} at ${time}.`,
          text: arrived ? deviceText.arrived(name, place, time) : deviceText.left(name, place, time),
          at: e.eventTime,
        });
      }
    });
  });
  return out;
}

// ---- point-in-place geofence tests (for "arrived safe" = parked at a ⭐ place) ----
function haversineM(aLat, aLng, bLat, bLng) {
  const R = 6371000, toR = Math.PI / 180;
  const dLat = (bLat - aLat) * toR, dLng = (bLng - aLng) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(aLat * toR) * Math.cos(bLat * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
// ray-casting; pts are [lat, lng] pairs
function pointInPoly(lat, lng, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const yi = pts[i][0], xi = pts[i][1], yj = pts[j][0], xj = pts[j][1];
    const hit = ((xi > lng) !== (xj > lng)) && (lat < ((yj - yi) * (lng - xi)) / (xj - xi) + yi);
    if (hit) inside = !inside;
  }
  return inside;
}
// Turn Traccar geofences into fast contains-tests for the owner's ⭐ saved places.
export function buildPlaceTests(geofences) {
  const out = [];
  (geofences || []).forEach((g) => {
    const nm = g.name || '';
    if (!(nm.startsWith('⭐') || nm.startsWith('⏱'))) return;
    const name = nm.replace(/^[⭐⏱]\s*/, '').trim();
    const area = g.area || '';
    const cm = area.match(/CIRCLE\s*\(\s*(-?[\d.]+)\s+(-?[\d.]+)\s*,\s*(-?[\d.]+)\s*\)/i);
    if (cm) {
      const clat = +cm[1], clng = +cm[2], rad = +cm[3];
      out.push({ name, test: (lat, lng) => haversineM(lat, lng, clat, clng) <= rad });
      return;
    }
    const pm = area.match(/POLYGON\s*\(\((.+)\)\)/i);
    if (pm) {
      const pts = pm[1].split(',').map((s) => s.trim().split(/\s+/).map(Number)).filter((p) => p.length >= 2 && !isNaN(p[0]));
      if (pts.length >= 3) out.push({ name, test: (lat, lng) => pointInPoly(lat, lng, pts) });
    }
  });
  return out;
}
// Which ⭐ place (if any) contains this point? Returns the clean place name or null.
export function placeContaining(lat, lng, tests) {
  if (lat == null || lng == null || !tests) return null;
  for (const t of tests) { if (t.test(lat, lng)) return t.name; }
  return null;
}

const KEY = 'dd-device-link-state';
const getState = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
const saveState = (s) => localStorage.setItem(KEY, JSON.stringify(s));

// Decode make / model / year from a VIN using the free NHTSA vPIC database.
export async function decodeVin(vin) {
  if (!vin || vin.length < 11) return {};
  try {
    const r = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${encodeURIComponent(vin)}?format=json`);
    const d = await r.json();
    const v = (d.Results && d.Results[0]) || {};
    return { make: v.Make || '', model: v.Model || '', year: v.ModelYear || '' };
  } catch { return {}; }
}

// Compare each device to its last-known link state; return new alerts (with the
// ready-to-send `text`) and remember the new state. First sighting never alerts.
// An OBD tracker sleeps when the car is off and sends periodic heartbeats, so it
// cycles offline↔online while parked. Only treat it as a real disconnect if it
// stays offline past this window (a genuine unplug / power loss doesn't come back).
const OFFLINE_ALERT_MS = 30 * 60000; // 30 minutes

export async function checkDeviceLinks(devices, positions) {
  const state = getState();
  const out = [];
  const now = Date.now();
  for (const d of devices) {
    const p = positions.find((x) => x.deviceId === d.id);
    const vin = (p && p.attributes && p.attributes.vin) || null;
    const status = d.status || 'unknown';
    const time = new Date(d.lastUpdate || Date.now()).toLocaleTimeString();
    const loc = (p && p.address) || '';
    const prev = state[d.id];

    if (prev === undefined) { state[d.id] = { status, vin, offlineSince: status === 'offline' ? now : null, disconnectAlerted: false, inUseAtDrop: false, alwaysPowered: !!(status === 'online' && p && p.attributes && p.attributes.ignition === false) }; continue; }

    let offlineSince = prev.offlineSince || null;
    let disconnectAlerted = prev.disconnectAlerted || false;
    let inUseAtDrop = prev.inUseAtDrop || false;
    let alwaysPowered = prev.alwaysPowered || false;

    // Learn whether this tracker stays powered while parked. If we ever see it
    // ONLINE with the engine OFF, its OBD port has constant power (no internal
    // battery needed) — so a later drop is a genuine disconnect, not the car just
    // being shut off. Ignition-switched ports are only ever alive with the engine
    // on, so they never get this flag and won't false-alarm on normal parking.
    if (status === 'online' && p && p.attributes && p.attributes.ignition === false) {
      alwaysPowered = true;
    }

    if (status === 'offline') {
      if (prev.status !== 'offline') {
        offlineSince = now; disconnectAlerted = false;
        // Was the car actually DRIVING when it went dark? A drop mid-drive looks
        // like an unplug/power loss. But a tracker almost always sends its LAST
        // record with ignition still latched "on" and then sleeps the instant you
        // park — so "ignition true" or a stale low speed on its own made every
        // normal park look like a mid-drive disconnect. Require genuine motion AND
        // a real driving speed (≥20 mph) so only a true in-use drop counts.
        const attrs = (p && p.attributes) || {};
        const lastMph = (p ? (p.speed || 0) : 0) * 1.15078; // Traccar speed is knots
        inUseAtDrop = attrs.ignition === true && attrs.motion !== false && lastMph >= 20;
      }
      // NOTE: we no longer raise a "Tracker disconnected" banner from the browser
      // just because Traccar flipped the device to "offline". These are OBD
      // trackers that SLEEP when parked and routinely drop cell signal mid-drive
      // (highways, tunnels, garages), so a status flip is not a reliable
      // disconnect signal — it kept crying wolf on car after car ("went silent
      // while in use") when the car was actually fine. A genuine unplug/power-cut
      // is detected server-side in push.js, which requires sustained silence AND
      // real driving and does NOT fire if the tracker checks back in.
    } else if (status === 'online') {
      // back online is routine — no alert, just reset the offline timer (keep the
      // learned alwaysPowered flag)
      offlineSince = null; disconnectAlerted = false; inUseAtDrop = false;
    }

    if (vin && prev.vin && vin !== prev.vin) {
      const info = await decodeVin(vin);
      const desc = [info.year, info.make, info.model].filter(Boolean).join(' ');
      out.push({ key: `newveh:${d.id}:${vin}`, entity: d.name, severity: 'warn', icon: '🚗', title: 'Tracker moved to a new vehicle', info: `${d.name} was connected to a ${desc || 'different vehicle'} (VIN ${vin}).`, text: deviceText.newVehicle(d.name, { ...info, vin }) });
    }
    state[d.id] = { status, vin: vin || prev.vin, offlineSince, disconnectAlerted, inUseAtDrop, alwaysPowered };
  }
  saveState(state);
  return out;
}
